import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { fmtINR } from "../lib/format";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";

const COLORS = ["#fb8b1e", "#4af6c3", "#0068ff", "#ff433d"];

export default function Loans() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/loans").then((r) => setData(r.data));
    }, []);
    if (!data) return <div className="font-mono-fin text-sm text-[var(--text-secondary)]">// LOADING LOANS...</div>;

    const pieData = data.loans.map((l) => ({ name: `${l.lender} ${l.type}`, value: l.outstanding }));

    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// DEBT MANAGEMENT</div>
                <h1 className="font-display font-black text-3xl">Loans & Debt Schedule</h1>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard testId="loan-outstanding" label="Total Outstanding" value={data.summary.total_outstanding} sub={`${data.loans.length} loans`} accent />
                <KpiCard testId="loan-sanction" label="Total Sanction" value={data.summary.total_sanction} />
                <KpiCard testId="loan-emi" label="Monthly EMI Burden" value={data.summary.monthly_emi} sub="Term loans only" />
                <KpiCard testId="loan-rate" label="Weighted Avg Rate" value={`${data.summary.weighted_avg_rate.toFixed(2)}%`} format="custom" sub="Effective" />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                <Panel testId="loan-table" className="xl:col-span-2" title="Loan-wise Breakup">
                    <div className="overflow-x-auto">
                        <table className="w-full font-mono-fin text-xs">
                            <thead className="border-b border-panel">
                                <tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                    <th className="text-left py-2">Lender</th>
                                    <th className="text-left">Type</th>
                                    <th className="text-right">Sanction</th>
                                    <th className="text-right">Outstanding</th>
                                    <th className="text-right">Rate</th>
                                    <th className="text-right">EMI</th>
                                    <th className="text-left">Purpose</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.loans.map((l) => (
                                    <tr key={l.id} className="border-b border-[var(--border)]/50 hover:bg-[var(--bg-panel-hover)]">
                                        <td className="py-2 text-[var(--text-primary)]">{l.lender}</td>
                                        <td className="text-[var(--text-secondary)]">{l.type}</td>
                                        <td className="text-right text-[var(--text-primary)]">{fmtINR(l.sanction)}</td>
                                        <td className="text-right text-neg">{fmtINR(l.outstanding)}</td>
                                        <td className="text-right text-amber">{l.rate.toFixed(2)}%</td>
                                        <td className="text-right text-[var(--text-primary)]">{l.emi ? fmtINR(l.emi) : "—"}</td>
                                        <td className="text-[var(--text-secondary)] text-[10px] uppercase tracking-wider">{l.purpose}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Panel>
                <Panel testId="loan-pie" title="Debt Mix">
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={(e) => `${(e.percent * 100).toFixed(0)}%`} labelLine={false}>
                                    {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                </Pie>
                                <Tooltip formatter={(v) => fmtINR(v)} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="text-[10px] font-mono-fin text-[var(--text-muted)] uppercase tracking-wider text-center mt-2">DSCR 1.68x · ICR 3.14x · D/E 1.28x</div>
                </Panel>
            </div>
        </div>
    );
}
